/**
 * Settings (the app's Settings panel): the UI language of this device, and
 * the defaults for new projects (seconds per question, font, allow cloning),
 * kept in users/{uid}.settings through the manageAccount function.
 */
import { useEffect, useState } from "react";
import { DEFAULT_SETTINGS, saveSettings, type Account, type AccountSettings } from "../data/myProjects";
import { FONT_FAMILIES, FONT_LABELS, fontFamilyFor } from "../player/fonts";
import { currentLanguage, saveUiLanguage, t, UI_LANGUAGES } from "../player/i18n";
import { APP_VERSION } from "../version";

const LANG_NAMES: Record<string, string> = { ca: "Català", es: "Español", en: "English", fr: "Français", pt: "Português", it: "Italiano", de: "Deutsch", eu: "Euskara", gl: "Galego", nl: "Nederlands", da: "Dansk", sv: "Svenska", nb: "Norsk bokmål", fi: "Suomi", pl: "Polski", cs: "Čeština", sk: "Slovenčina", hu: "Magyar", ro: "Română", hr: "Hrvatski", el: "Ελληνικά", bg: "Български", tr: "Türkçe", az: "Azərbaycanca", uz: "Oʻzbekcha", ru: "Русский", uk: "Українська", id: "Bahasa Indonesia", ms: "Bahasa Melayu", fil: "Filipino", vi: "Tiếng Việt", ar: "العربية", he: "עברית", fa: "فارسی", ur: "اردو" };
const FONT_KEYS = ["default", ...Object.keys(FONT_FAMILIES).filter((k) => k !== "defaultArabic")];
const SECONDS = [10, 15, 20, 30, 45, 60, 90, 120];

export function SettingsView({ account }: { account: Account | null }) {
  const [s, setS] = useState<AccountSettings>(() => ({ ...DEFAULT_SETTINGS, ...(account?.settings ?? {}) }));
  const [saved, setSaved] = useState<"idle" | "saving" | "done" | "error">("idle");
  useEffect(() => {
    if (account?.settings) setS((cur) => ({ ...cur, ...account.settings }));
  }, [account?.settings]);

  const update = (patch: Partial<AccountSettings>) => {
    const next = { ...s, ...patch };
    setS(next);
    setSaved("saving");
    saveSettings(next)
      .then(() => setSaved("done"))
      .catch(() => setSaved("error"));
  };
  const changeLanguage = (l: string) => {
    saveUiLanguage(l);
    update({ language: l });
    location.reload();
  };

  return (
    <section className="view">
      <h1>{t("settings")}</h1>
      <div className="panel-list">
        <label className="panel-row">
          <span>{t("appLanguage")}</span>
          <select className="select" value={currentLanguage()} onChange={(e) => changeLanguage(e.target.value)}>
            {UI_LANGUAGES.map((l) => (
              <option key={l} value={l}>
                {LANG_NAMES[l] ?? l}
              </option>
            ))}
          </select>
        </label>
        <div className="panel-row">
          <span>{t("webVersion")}</span>
          <span className="muted">{APP_VERSION}</span>
        </div>
      </div>
      <p className="muted small">{t("defaultsInfo")}</p>
      <div className="panel-list">
        <label className="panel-row">
          <span>{t("defaultSeconds")}</span>
          <select className="select" value={s.defaultSeconds} onChange={(e) => update({ defaultSeconds: Number(e.target.value) })}>
            {SECONDS.map((n) => (
              <option key={n} value={n}>
                {n}s
              </option>
            ))}
          </select>
        </label>
        <label className="panel-row">
          <span>{t("defaultFont")}</span>
          <select className="select" value={s.defaultFont ?? "default"} onChange={(e) => update({ defaultFont: e.target.value === "default" ? null : e.target.value })} style={{ fontFamily: fontFamilyFor(s.defaultFont) }}>
            {FONT_KEYS.map((k) => (
              <option key={k} value={k} style={{ fontFamily: fontFamilyFor(k === "default" ? null : k) }}>
                {k === "default" ? `${t("defaultFontName")} - ABCabc` : `${FONT_LABELS[k] ?? k} - ABCabc`}
              </option>
            ))}
          </select>
        </label>
        <label className="panel-row">
          <span>{t("allowCloning")}</span>
          <input type="checkbox" className="switch" checked={s.allowCloning} onChange={(e) => update({ allowCloning: e.target.checked })} />
        </label>
      </div>
      <p className="muted small">{saved === "saving" ? "…" : saved === "done" ? t("saved") : saved === "error" ? t("errSave") : ""}</p>
    </section>
  );
}
